import classNames from 'classnames';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { PropsScrollBar, ThemesScrollBar } from './PropsScrollBar';

import styles from './ScrollBar.module.scss';

export const ScrollBar = ({
  children,
  maxH,
  maxW,
  getContainerRef,
  type,
  hide,
  classContainer,
  classScrollBar,
  classWrapper,
  theme = ThemesScrollBar.gray,
  ...props
}: PropsScrollBar) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const [thumbHeight, setThumbHeight] = useState(20);
  const [thumbWidth, setThumbWidth] = useState(20);
  const [scrollStartPosition, setScrollStartPosition] = useState<number | null>(null);
  const [initialScrollTop, setInitialScrollTop] = useState<number>(0);
  const [initialScrollLeft, setInitialScrollLeft] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHide, setIsHide] = useState(false);
  function handleResize(ref: HTMLDivElement, trackSize: number) {
    const { clientHeight, scrollHeight, scrollWidth, clientWidth } = ref;
    if (type === 'horizontal') {
      setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20));
      setIsHide(clientWidth === scrollWidth);
    } else {
      setIsHide(clientHeight === scrollHeight);
      setThumbHeight(Math.max((clientHeight / scrollHeight) * trackSize, 20));
    }
  }
  const handleTrackClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      const { current: trackCurrent } = scrollTrackRef;
      const { current: contentCurrent } = contentRef;
      if (trackCurrent && contentCurrent) {
        const { clientY, clientX } = e;
        const target = e.target as HTMLDivElement;
        const rect = target.getBoundingClientRect();
        const trackTop = rect.top;
        const trackLeft = rect.left;
        const thumbOffset = type === 'horizontal' ? -(thumbWidth / 2) : -(thumbHeight / 2);
        const clickRatio =
          type === 'horizontal'
            ? (clientX - trackLeft + thumbOffset) / trackCurrent.clientWidth
            : (clientY - trackTop + thumbOffset) / trackCurrent.clientHeight;
        const scrollAmount =
          type === 'horizontal' ? Math.floor(clickRatio * contentCurrent.scrollWidth) : Math.floor(clickRatio * contentCurrent.scrollHeight);
        contentCurrent.scrollTo({ top: scrollAmount, behavior: 'smooth' });
        type === 'horizontal'
          ? contentCurrent.scrollTo({ left: scrollAmount, behavior: 'smooth' })
          : contentCurrent.scrollTo({ top: scrollAmount, behavior: 'smooth' });
      }
    },
    [thumbHeight, thumbWidth],
  );

  const handleThumbPosition = useCallback(() => {
    if (!contentRef.current || !scrollTrackRef.current || !scrollThumbRef.current) {
      return;
    }
    if (type === 'horizontal') {
      const { scrollLeft: contentLeft, scrollWidth: contentWidth } = contentRef.current;
      const { clientWidth: trackWidth } = scrollTrackRef.current;
      let newLeft = (+contentLeft / +contentWidth) * trackWidth;
      newLeft = Math.min(newLeft, trackWidth - thumbWidth);
      const thumb = scrollThumbRef.current;
      thumb.style.left = `${newLeft}px`;
    } else {
      const { scrollTop: contentTop, scrollHeight: contentHeight } = contentRef.current;
      const { clientHeight: trackHeight } = scrollTrackRef.current;
      let newTop = (+contentTop / +contentHeight) * trackHeight;
      newTop = Math.min(newTop, trackHeight - thumbHeight);
      const thumb = scrollThumbRef.current;
      thumb.style.top = `${newTop}px`;
    }
  }, []);

  const handleThumbMousedown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'horizontal') {
      setScrollStartPosition(e.clientX);
      if (contentRef.current) {
        setInitialScrollLeft(contentRef.current.scrollLeft);
      }
    } else {
      setScrollStartPosition(e.clientY);
      if (contentRef.current) {
        setInitialScrollTop(contentRef.current.scrollTop);
      }
    }
    setIsDragging(true);
  }, []);

  const handleThumbMouseup = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (isDragging) {
        setIsDragging(false);
      }
    },
    [isDragging],
  );

  const handleThumbMousemove = useCallback(
    e => {
      e.stopPropagation();
      if (isDragging) {
        const {
          scrollHeight: contentScrollHeight,
          offsetHeight: contentOffsetHeight,
          offsetWidth: contentOffsetWidth,
          scrollWidth: contentScrollWidth,
        } = contentRef.current;
        if (type === 'horizontal') {
          const deltaX = (e.clientX - scrollStartPosition) * (contentOffsetWidth / thumbWidth);
          contentRef.current.scrollLeft = Math.min(initialScrollLeft + deltaX, contentScrollWidth - contentOffsetWidth);
        } else {
          const deltaY = (e.clientY - scrollStartPosition) * (contentOffsetHeight / thumbHeight);
          contentRef.current.scrollTop = Math.min(initialScrollTop + deltaY, contentScrollHeight - contentOffsetHeight);
        }
      }
    },
    [isDragging, scrollStartPosition, thumbHeight],
  );
  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const ref = contentRef.current;
      getContainerRef && getContainerRef(contentRef.current);
      observer.current = new ResizeObserver(entries => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length || !scrollTrackRef.current) {
            return;
          }

          const { clientHeight, clientWidth } = scrollTrackRef.current;
          const trackSize = type === 'horizontal' ? clientWidth : clientHeight;
          handleResize(ref, trackSize);
        });
      });
      observer.current.observe(ref);
      ref.addEventListener('scroll', handleThumbPosition);
      return () => {
        observer.current?.unobserve(ref);
        ref.removeEventListener('scroll', handleThumbPosition);
      };
    }
  }, [contentRef, scrollTrackRef, maxH, maxW]);

  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);
    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup]);
  return (
    <div
      className={classNames(styles.Container, styles[type], classWrapper, styles[theme])}
      style={
        {
          '--maxH': maxH && `${maxH}px`,
          '--maxW': maxW && `${maxW}px`,
        } as CSSProperties
      }
    >
      <div className={classNames(styles.Content, classContainer)} ref={contentRef} {...props}>
        {children}
      </div>

      <div className={classNames(styles.ScrollBar, isHide && styles.Opacity, hide && styles.Hide, classScrollBar)}>
        <div className={styles.TrackAndThumb}>
          <div className={styles.Track} ref={scrollTrackRef} onClick={handleTrackClick} style={{ cursor: isDragging && 'grabbing' }}></div>
          <div
            className={styles.Thumb}
            ref={scrollThumbRef}
            onMouseDown={handleThumbMousedown}
            style={{
              height: type === 'vertical' && `${thumbHeight}px`,
              width: type === 'horizontal' && `${thumbWidth}px`,
              cursor: isDragging ? 'grabbing' : isHide ? 'pointer' : 'grab',
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};
